import { forwardRef, useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { cacheMediaResponse, cachedMedia, deliveryUrl, mediaIdentity, mediaPath } from "@/lib/mediaUrls";

const pending = new Map();

export function useMediaUrl(source) {
  const actor = mediaIdentity();
  const [resolved, setResolved] = useState(null);
  const path = mediaPath(source);
  const alreadySigned = Boolean(source?.includes("?media_token="));

  useEffect(() => {
    if (!path || alreadySigned) return;
    let active = true, timer;
    const resolve = async () => {
      try {
        let cached = cachedMedia(source);
        if (!cached) {
          const key = `${actor}:${path}`;
          if (!pending.has(key)) {
            const request = api.get("/media/access", { params: { path } }).then(({ data }) => {
              cacheMediaResponse(data, actor);
            }).finally(() => pending.delete(key));
            pending.set(key, request);
          }
          await pending.get(key);
          cached = cachedMedia(source);
        }
        if (!active || mediaIdentity() !== actor) return;
        setResolved({ source, actor, url: cached?.url });
        if (cached) timer = setTimeout(resolve, Math.max(1000, cached.expires * 1000 - Date.now() - 30000));
      } catch {
        if (active) setResolved({ source, actor, url: undefined });
      }
    };
    resolve();
    return () => { active = false; clearTimeout(timer); };
  }, [source, path, alreadySigned, actor]);

  if (!path || alreadySigned) return deliveryUrl(source) || undefined;
  return cachedMedia(source)?.url || (resolved?.source === source && resolved.actor === actor ? resolved.url : undefined);
}

export const MediaImage = forwardRef(({ src, alt = "", ...props }, ref) => {
  const url = useMediaUrl(src);
  return <img {...props} ref={ref} src={url} alt={alt} />;
});
MediaImage.displayName = "MediaImage";

export const MediaVideo = forwardRef(({ src, poster, ...props }, ref) => {
  const url = useMediaUrl(src);
  const cover = useMediaUrl(poster);
  return <video {...props} ref={ref} src={url} poster={cover} />;
});
MediaVideo.displayName = "MediaVideo";

export const MediaLink = ({ href, children, ...props }) => {
  const url = useMediaUrl(href);
  return <a {...props} href={url}>{children}</a>;
};
